import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import React from "react";
import "../../../../css/Customer/Pages/OurServicesPage/Service.css";
import FAQContainer from "frontend/Components/DesignSystem/FAQ/FAQContainer";
import CardProduct from "frontend/Components/DesignSystem/Cards/CardProduct";
import { ReactComponent as MortagePlanningIcon } from "../../../../assets/icons/Mortgage-Planning.svg";
import { ReactComponent as CorporatePlanningIcon } from "../../../../assets/icons/Corporate-Planning.svg";
import { ReactComponent as InvestmentPlanningIcon } from "../../../../assets/icons/Investment-Planning.svg";
import { ReactComponent as EquityReleaseIcon } from "../../../../assets/icons/Equity -Release.svg";
import { ReactComponent as InsuranceProtectionIcon } from "../../../../assets/icons/Insurance-Protection.svg";
import { ReactComponent as RetirementPlanningIcon } from "../../../../assets/icons/Retirement-Planning.svg";
import AdviserValues from "frontend/Components/AdviserValues/AdviserValue";
import { ReactComponent as NumberOneIcon } from "../../../../assets/icons/Number 1.svg";
import { ReactComponent as NumberTwoIcon } from "../../../../assets/icons/Number 2.svg";
import { ReactComponent as NumberThreeIcon } from "../../../../assets/icons/Number 3.svg";
import { ReactComponent as NumberFourIcon } from "../../../../assets/icons/Number 4.svg";
import Testimonials from "../../../Components/Testimonials/Testimonials";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
// import CustomerRetirementImage from "../../../../../assets/images/customer-retirement-min.jpeg";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, getPageUrl } from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";

/**
 * InvestmentPlanning component renders the Investment Planning service page.
 *
 * This page includes navigation, a hero section, service descriptions, FAQs,
 * a list of related services, adviser values, testimonials, contact information, and a footer.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered Investment Planning service page.
 *
 * @example
 * // Usage in a React Router route
 * <Route path="/OurServices/InvestmentPlanning" element={<InvestmentPlanning />} />
 *
 * @description
 * - Displays navigation items for main site sections.
 * - Shows a hero animation with a title and subtitle.
 * - Provides information about investment planning and risk.
 * - Renders a FAQ section with common investment questions.
 * - Lists other related financial services as cards.
 * - Highlights adviser values with icons and descriptions.
 * - Includes testimonials, contact form, and footer.
 */
const InvestmentPlanning = () => {
  const navigationItems = [
    { label: "Home", navigateTo: "/" },
    { label: "Our Services", navigateTo: "/OurServices" },
    { label: "About Us", navigateTo: "/AboutUs" },
    { label: "Contact Us", navigateTo: "/ContactUs" },
  ];

  const faqs = [
    {
      question: "What approach do you take?",
      answer:
        "When investing, we need to find out how long you are prepared to invest for, and how much risk you are comfortable to take.   There can be various solutions, and we can discuss the options with you. ",
    },
    {
      question: "Where do you invest my money?",
      answer:
        "We use highly rated, fully regulated investment funds from very established fund management companies.   As Independent Financial Advisers, we select the best investment funds and products from the whole market.  Advisers who are not independent will often create their own investment funds.   These normally have higher charges which add to their company’s profits.   We don’t do this; we have a particular focus on keeping costs low.   This is a key factor in helping to deliver consistent performance.",
    },
    {
      question: "How do you select the best funds?",
      answer:
        "We will discuss your preferences for investing.  This will include whether investing in a responsible or sustainable way is important to you.   We will also discuss your attitude to investment risk and the timeframe you are prepared to invest for.",
    },
    {
      question: "Will I be able to track the fund performance?",
      answer:
        "Yes - You will be able to log into the provider’s website for up-to-date fund values.   ",
    },
    {
      question: "What if my investment objectives change?",
      answer:
        "You can opt to have ongoing advice on your investments for an additional fee.   This will enable your adviser to take account of any changes in your investment objectives.   Changes to your investment funds could then be made if necessary.  ",
    },
    {
      question: "Where are the meetings held?",
      answer:
        "This can be over the phone or in person dependent on your needs and availability. ",
    },

    {
      question: "Do I pay for an initial meeting?",
      answer:
        "Our initial consultation is free.  After that you can review our client agreement and decide if you would like to have regulated advice. ",
    },
  ];
  const values = [
    {
      icon: <NumberOneIcon />,
      text: "You are our most important person",
    },
    {
      icon: <NumberTwoIcon />,
      text: "Recommendations are planned out and delivered to you",
    },
    {
      icon: <NumberThreeIcon />,
      text: "Deliver the best outcomes for you",
    },
    {
      icon: <NumberFourIcon />,
      text: "We talk you through every step of the process",
    },
  ];

  return (
    <div className="Service">
      <SeoHelmet
        page={SEO_PAGES.investmentPlanning}
        service={{
          name: "Investment Planning Advice",
          description:
            "Rosemount Financial Solutions provides investment planning advice, helping clients build suitable investment strategies based on their goals, attitude to risk, investment timeframe and preferences.",
          serviceType: [
            "Investment Planning",
            "Investment Advice",
            "Independent Financial Advice",
            "Financial Planning",
            "Responsible Investing",
            "Sustainable Investing",
            "Risk-Based Investment Planning",
          ],
        }}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Our Services",
            url: getPageUrl("/OurServices"),
          },
          {
            name: "Investment Planning",
            url: getPageUrl("/OurServices/InvestmentPlanning"),
          },
        ]}
        faqs={faqs}
      />
      <NavBar navigationItems={navigationItems} />
      <HeroAnimation
        imgSrc={ImageDirectory["customer-investment-min"]}
        altText="Two advisers looking at a graph on paper of a bar chart and a laptop with a bar graph on."
        children={
          <>
            <h1>Investment Planning</h1>
            <div className="SubTitle">
              Building a strategy that works for you
            </div>
          </>
        }
      />
      <div className="SubTitle-Container">
        <div className="SubTitle">
          At Rosemount, we provide professional investment planning to help grow
          your wealth and achieve your financial aspirations. We can discuss
          your specific goal and talk through our bespoke plans for investments.
        </div>
        <div className="SubTitle">
          We want to ensure you get the most for you by learning your risk
          tolerance, aspirations and investment timeframe.
        </div>
        <div className="SubTitle">
          The value of the investments and the income they produce can fall as
          well as rise. You may get back less than you invested.  
        </div>
      </div>
      <FAQContainer faqs={faqs} />
      <div className="Customer-Services-Container">
        <h2>Our Services</h2>
        <div className="Customer-Services">
          <CardProduct
            Icon={MortagePlanningIcon}
            title="Mortgage Planning"
            link="/OurServices/MortgagePlanning"
          />
          <CardProduct
            Icon={CorporatePlanningIcon}
            title="Corporate Planning"
            link="/OurServices/CorporatePlanning"
          />
          <CardProduct
            Icon={InsuranceProtectionIcon}
            title="Insurance/ Protection"
            link="/OurServices/InsuranceProtection"
          />
          <CardProduct
            Icon={RetirementPlanningIcon}
            title="Retirement Planning"
            link="/OurServices/RetirementPlanning"
          />
          <CardProduct
            Icon={EquityReleaseIcon}
            title="Equity Release"
            link="/OurServices/EquityRelease"
          />
        </div>
      </div>
      <AdviserValues title="Our Adviser Values" values={values} />
      <Testimonials />
      <ContactUs />
      <Footer />
    </div>
  );
};

export default InvestmentPlanning;
